import classNames from 'classnames'
import { FC, ReactNode } from 'react'
import {
  Band,
  BrandLabel,
  BrandRole,
  CitationHealth,
  CitationType,
  ContextTagType,
} from 'src/services/aiListeningApi/brandProfile.types'
import styles from './pill.module.scss'

export type PillTone =
  | 'amber'
  | 'dark'
  | 'green'
  | 'grey'
  | 'orange'
  | 'pink'
  | 'purple'
  | 'red'
  | 'teal'

interface PillProps {
  children: ReactNode
  className?: string
  dot?: boolean
  // Outline pills (role / brand labels) carry a border in the tone's colour.
  outline?: boolean
  size?: 'md' | 'sm'
  tone: PillTone
  uppercase?: boolean
}

// The small rounded status label used across the AI Listening screens.
export const Pill: FC<PillProps> = ({
  children,
  className,
  dot = false,
  outline = false,
  size = 'sm',
  tone,
  uppercase = false,
}) => (
  <span
    className={classNames(styles.pill, styles[tone], styles[size], className, {
      [styles.outline]: outline,
      [styles.uppercase]: uppercase,
    })}
  >
    {dot && <span className={styles.dot} />}
    {children}
  </span>
)

export const BAND_TONES: Record<Band, PillTone> = {
  attention: 'orange',
  moderate: 'grey',
  strong: 'purple',
}

export const CONTEXT_TAG_TONES: Record<ContextTagType, PillTone> = {
  attribute: 'amber',
  intent: 'pink',
  location: 'teal',
  topic: 'purple',
}

export const CITATION_TYPE_TONES: Record<CitationType, PillTone> = {
  competitor: 'orange',
  owned: 'purple',
  third_party: 'grey',
}

export const CITATION_HEALTH_TONES: Record<CitationHealth, PillTone> = {
  low: 'orange',
  moderate: 'grey',
  strong: 'purple',
}

export const BRAND_LABEL_TONES: Record<BrandLabel, PillTone> = {
  ai_discovered: 'grey',
  tracked: 'green',
  you: 'purple',
}

export const ROLE_TONES: Record<BrandRole | 'not_mentioned', PillTone> = {
  alternative: 'purple',
  compared: 'amber',
  neutral: 'grey',
  not_mentioned: 'red',
  primary: 'green',
}
